import React, { useState, useEffect } from 'react';
import { 
  FaPlus, 
  FaSearch, 
  FaEye, 
  FaCheck, 
  FaTimes, 
  FaClock,
  FaEnvelope,
  FaComments,
  FaExclamationTriangle,
  FaCheckCircle,
  FaReply,
  FaUser,
  FaSpinner
} from 'react-icons/fa';
import { API_ENDPOINTS } from '../../config/api';

const TicketSystem = () => {
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [showNewTicketModal, setShowNewTicketModal] = useState(false);
  const [loadingTicket, setLoadingTicket] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [isInternalComment, setIsInternalComment] = useState(false);
  const [saving, setSaving] = useState(false);
  const [availableAgents, setAvailableAgents] = useState([
    'admin@peaknet.com.au',
    'support@peaknet.com.au',
    'billing@peaknet.com.au',
    // TODO: Fetch from User settings/DB when auth is implemented
  ]);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [pendingPriorityChange, setPendingPriorityChange] = useState(null);
  const [statusChangeComment, setStatusChangeComment] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  const [newTicket, setNewTicket] = useState({
    subject: '',
    description: '',
    priority: 'medium',
    customerEmail: '',
    category: 'general'
  });

  // Get current admin user (you can replace this with actual auth)
  const getAdminUser = () => {
    // TODO: Get from auth context or localStorage
    return localStorage.getItem('adminUser') || 'admin-portal';
  };

  useEffect(() => {
    loadTickets();
  }, []);

  useEffect(() => {
    filterTickets();
  }, [tickets, searchQuery, statusFilter, priorityFilter]);

  const loadTickets = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(API_ENDPOINTS.TICKET_MANAGEMENT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken') || ''}`
        },
        body: JSON.stringify({
          action: 'getTickets',
          limit: 100
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to load tickets');
      }

      if (data.success && data.tickets) {
        // Map backend ticket format to frontend display format
        const mappedTickets = data.tickets.map(ticket => ({
          id: ticket.ticketId,
          ticketId: ticket.ticketId,
          customer: ticket.customerName || 'Unknown Customer',
          customerEmail: ticket.customerEmail || '',
          customerId: ticket.customerId || null,
          accountNumber: ticket.accountNumber || null,
          subject: ticket.subject,
          description: ticket.description,
          priority: ticket.priority,
          status: ticket.status,
          category: ticket.category,
          assignedTo: ticket.assignedTo || null,
          createdAt: ticket.createdAt,
          updatedAt: ticket.updatedAt,
          lastActivityAt: ticket.lastActivityAt || ticket.updatedAt,
          responseCount: ticket.comments ? ticket.comments.length : 0,
          comments: ticket.comments || [],
          tags: ticket.tags || [],
          source: ticket.source || 'manual',
          createdBy: ticket.createdBy || 'system',
          resolvedAt: ticket.resolvedAt || null,
          closedAt: ticket.closedAt || null,
          // Keep full backend object for detail view
          _backendTicket: ticket
        }));

        setTickets(mappedTickets);
      } else {
        setTickets([]);
      }
    } catch (err) {
      console.error('Error loading tickets:', err);
      setError(err.message || 'Failed to load tickets. Please try again.');
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  const filterTickets = () => {
    let filtered = tickets;

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(ticket =>
        ticket.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ticket.customer.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ticket.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (ticket.customerEmail && ticket.customerEmail.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(ticket => ticket.status === statusFilter);
    }

    // Priority filter
    if (priorityFilter !== 'all') {
      filtered = filtered.filter(ticket => ticket.priority === priorityFilter);
    }

    // Sort by lastActivityAt (most recent first)
    filtered.sort((a, b) => {
      const dateA = new Date(a.lastActivityAt || a.updatedAt);
      const dateB = new Date(b.lastActivityAt || b.updatedAt);
      return dateB - dateA;
    });

    setFilteredTickets(filtered);
  };

  const loadTicketDetails = async (ticketId) => {
    try {
      setLoadingTicket(true);
      
      const response = await fetch(API_ENDPOINTS.TICKET_MANAGEMENT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken') || ''}`
        },
        body: JSON.stringify({
          action: 'getTicket',
          ticketId: ticketId
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to load ticket details');
      }

      if (data.success && data.ticket) {
        const ticket = data.ticket;
        // Map backend format to frontend format
        const mappedTicket = {
          id: ticket.ticketId,
          ticketId: ticket.ticketId,
          customer: ticket.customerName || 'Unknown Customer',
          customerEmail: ticket.customerEmail || '',
          customerId: ticket.customerId || null,
          accountNumber: ticket.accountNumber || null,
          subject: ticket.subject,
          description: ticket.description,
          priority: ticket.priority,
          status: ticket.status,
          category: ticket.category,
          assignedTo: ticket.assignedTo || null,
          createdAt: ticket.createdAt,
          updatedAt: ticket.updatedAt,
          lastActivityAt: ticket.lastActivityAt || ticket.updatedAt,
          responseCount: ticket.comments ? ticket.comments.length : 0,
          comments: ticket.comments || [],
          tags: ticket.tags || [],
          source: ticket.source || 'manual',
          createdBy: ticket.createdBy || 'system',
          resolvedAt: ticket.resolvedAt || null,
          closedAt: ticket.closedAt || null,
          _backendTicket: ticket
        };
        return mappedTicket;
      }
      
      return null;
    } catch (err) {
      console.error('Error loading ticket details:', err);
      throw err;
    } finally {
      setLoadingTicket(false);
    }
  };

  const updateTicketStatus = async (ticketId, newStatus) => {
    try {
      setSaving(true);
      
      const response = await fetch(API_ENDPOINTS.TICKET_MANAGEMENT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken') || ''}`
        },
        body: JSON.stringify({
          action: 'updateTicketStatus',
          ticketId: ticketId,
          status: newStatus,
          adminUser: getAdminUser()
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update ticket status');
      }

      if (data.success) {
        // Update local state
      setTickets(prev => prev.map(ticket =>
          ticket.id === ticketId ? { ...ticket, status: newStatus, updatedAt: data.ticket.updatedAt } : ticket
        ));
        
        // Update selected ticket if it's the one we just updated
        if (selectedTicket && selectedTicket.id === ticketId) {
          const updatedTicket = await loadTicketDetails(ticketId);
          if (updatedTicket) {
            setSelectedTicket(updatedTicket);
          }
        }
        
        // Reload tickets to get fresh data
        await loadTickets();
      }
    } catch (err) {
      console.error('Error updating ticket status:', err);
      alert(`Failed to update ticket status: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const updateTicketPriority = async (ticketId, newPriority) => {
    try {
      setSaving(true);
      
      const response = await fetch(API_ENDPOINTS.TICKET_MANAGEMENT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken') || ''}`
        },
        body: JSON.stringify({
          action: 'updateTicket',
          ticketId: ticketId,
          updates: {
            priority: newPriority
          },
          adminUser: getAdminUser()
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update ticket priority');
      }

      if (data.success) {
        // Update local state
        setTickets(prev => prev.map(ticket =>
          ticket.id === ticketId ? { ...ticket, priority: newPriority, updatedAt: data.ticket.updatedAt } : ticket
        ));
        
        // Update selected ticket if it's the one we just updated
        if (selectedTicket && selectedTicket.id === ticketId) {
          const updatedTicket = await loadTicketDetails(ticketId);
          if (updatedTicket) {
            setSelectedTicket(updatedTicket);
          }
        }
        
        // Reload tickets to get fresh data
        await loadTickets();
      }
    } catch (err) {
      console.error('Error updating ticket priority:', err);
      alert(`Failed to update ticket priority: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const createNewTicket = async () => {
    try {
      if (!newTicket.subject || !newTicket.description) {
        alert('Please fill in subject and description');
        return;
      }

      setSaving(true);
      
      const response = await fetch(API_ENDPOINTS.TICKET_MANAGEMENT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken') || ''}`
        },
        body: JSON.stringify({
          action: 'createTicket',
          subject: newTicket.subject,
          description: newTicket.description,
          priority: newTicket.priority,
          category: newTicket.category,
          customerEmail: newTicket.customerEmail || null,
          source: 'manual',
          adminUser: getAdminUser()
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create ticket');
      }

      if (data.success) {
        // Reset form
      setNewTicket({
        subject: '',
        description: '',
        priority: 'medium',
        customerEmail: '',
        category: 'general'
      });
        setShowNewTicketModal(false);
        
        // Reload tickets
        await loadTickets();
      }
    } catch (err) {
      console.error('Error creating ticket:', err);
      alert(`Failed to create ticket: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const addComment = async () => {
    if (!newComment.trim() || !selectedTicket) return;

    try {
      setSaving(true);
      
      const response = await fetch(API_ENDPOINTS.TICKET_MANAGEMENT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken') || ''}`
        },
        body: JSON.stringify({
          action: 'addComment',
          ticketId: selectedTicket.ticketId,
          comment: newComment,
          isInternal: isInternalComment,
          adminUser: getAdminUser()
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to add comment');
      }

      if (data.success) {
        // Reload ticket details
        const updatedTicket = await loadTicketDetails(selectedTicket.ticketId);
        if (updatedTicket) {
          setSelectedTicket(updatedTicket);
        }
        
        // Clear comment form
        setNewComment('');
        setIsInternalComment(false);
        
        // Reload tickets to update response count
        await loadTickets();
      }
    } catch (err) {
      console.error('Error adding comment:', err);
      alert(`Failed to add comment: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const assignTicket = async (ticketId, assignedTo) => {
    try {
      setSaving(true);
      
      const response = await fetch(API_ENDPOINTS.TICKET_MANAGEMENT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken') || ''}`
        },
        body: JSON.stringify({
          action: 'assignTicket',
          ticketId: ticketId,
          assignedTo: assignedTo || null,
          adminUser: getAdminUser()
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to assign ticket');
      }

      if (data.success) {
        // Reload tickets
        await loadTickets();
        
        // Update selected ticket if it's the one we just assigned
        if (selectedTicket && selectedTicket.id === ticketId) {
          const updatedTicket = await loadTicketDetails(ticketId);
          if (updatedTicket) {
            setSelectedTicket(updatedTicket);
          }
        }
      }
    } catch (err) {
      console.error('Error assigning ticket:', err);
      alert(`Failed to assign ticket: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const viewTicket = async (ticket) => {
    try {
      setLoadingTicket(true);
      const fullTicket = await loadTicketDetails(ticket.id);
      if (fullTicket) {
        setSelectedTicket(fullTicket);
        setPendingStatusChange(null);
        setPendingPriorityChange(null);
        setStatusChangeComment('');
        setHasUnsavedChanges(false);
        setShowTicketModal(true);
      } else {
        alert('Failed to load ticket details');
      }
    } catch (err) {
      console.error('Error loading ticket:', err);
      alert(`Failed to load ticket details: ${err.message}`);
    } finally {
      setLoadingTicket(false);
    }
  };

  const saveTicketChanges = async () => {
    if (!selectedTicket) return;

    // Check if status is changing - require comment
    const statusChanging = pendingStatusChange !== null && pendingStatusChange !== selectedTicket.status;
    if (statusChanging && !statusChangeComment.trim()) {
      alert('Please add a comment explaining the status change.');
      return;
    }

    try {
      setSaving(true);
      const promises = [];

      // Update status if changed
      if (pendingStatusChange !== null && pendingStatusChange !== selectedTicket.status) {
        const statusPromise = fetch(API_ENDPOINTS.TICKET_MANAGEMENT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json',
 'Authorization': `Bearer ${localStorage.getItem('adminToken') || ''}` },
          body: JSON.stringify({
            action: 'updateTicketStatus',
            ticketId: selectedTicket.ticketId,
            status: pendingStatusChange,
            notes: statusChangeComment.trim(),
            adminUser: getAdminUser()
          })
        });
        promises.push(statusPromise);
      }

      // Update priority if changed
      if (pendingPriorityChange !== null && pendingPriorityChange !== selectedTicket.priority) {
        const priorityPromise = fetch(API_ENDPOINTS.TICKET_MANAGEMENT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json',
 'Authorization': `Bearer ${localStorage.getItem('adminToken') || ''}` },
          body: JSON.stringify({
            action: 'updateTicket',
            ticketId: selectedTicket.ticketId,
            updates: { priority: pendingPriorityChange },
            adminUser: getAdminUser()
          })
        });
        promises.push(priorityPromise);
      }

      // If status changed, add comment
      if (statusChanging && statusChangeComment.trim()) {
        const commentPromise = fetch(API_ENDPOINTS.TICKET_MANAGEMENT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json',
 'Authorization': `Bearer ${localStorage.getItem('adminToken') || ''}` },
          body: JSON.stringify({
            action: 'addComment',
            ticketId: selectedTicket.ticketId,
            comment: `Status changed from ${selectedTicket.status} to ${pendingStatusChange}. ${statusChangeComment.trim()}`,
            isInternal: false,
            adminUser: getAdminUser()
          })
        });
        promises.push(commentPromise);
      }

      if (promises.length === 0) {
        alert('No changes to save');
        setSaving(false);
        return;
      }

      // Execute all updates
      const results = await Promise.all(promises);
      
      // Check for errors
      for (const result of results) {
        const data = await result.json();
        if (!result.ok) {
          throw new Error(data.error || 'Failed to save changes');
        }
      }

      // Reload ticket details
      const updatedTicket = await loadTicketDetails(selectedTicket.ticketId);
      if (updatedTicket) {
        setSelectedTicket(updatedTicket);
      }

      // Reset pending changes
      setPendingStatusChange(null);
      setPendingPriorityChange(null);
      setStatusChangeComment('');
      setHasUnsavedChanges(false);

      // Reload tickets list
      await loadTickets();
      
      alert('Ticket updated successfully');
    } catch (err) {
      console.error('Error saving ticket changes:', err);
      alert(`Failed to save changes: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'text-red-600 bg-red-100';
      case 'in_progress': return 'text-blue-600 bg-blue-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'resolved': return 'text-green-600 bg-green-100';
      case 'closed': return 'text-[#bdae93] bg-[#3c3836]';
      default: return 'text-[#bdae93] bg-[#3c3836]';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent':
      case 'high': return 'text-red-600 bg-red-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-[#bdae93] bg-[#3c3836]';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'urgent':
      case 'high': return <FaExclamationTriangle className="text-red-600" />;
      case 'medium': return <FaClock className="text-yellow-600" />;
      case 'low': return <FaCheckCircle className="text-green-600" />;
      default: return <FaExclamationTriangle className="text-[#bdae93]" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0A2463]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#ebdbb2]">Ticket System</h1>
          <p className="text-[#bdae93]">Manage customer support tickets and communications</p>
        </div>
        <button
          onClick={() => setShowNewTicketModal(true)}
          className="px-4 py-2 bg-[#fabd2f] text-[#1d2021] rounded-lg hover:bg-[#fe8019] transition-colors flex items-center space-x-2 font-medium"
        >
          <FaPlus size={16} />
          <span>New Ticket</span>
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-[#3c3836] border border-[#cc241d] text-[#fb4934] px-4 py-3 rounded-lg">
          <p>{error}</p>
          <button
            onClick={() => {
              setError(null);
              loadTickets();
            }}
            className="mt-2 text-sm underline text-[#fe8019]"
          >
            Retry
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="bg-[#282828] rounded-xl shadow-sm border border-[#3c3836] p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <FaSearch className="absolute left-3 top-3 text-[#928374]" />
            <input
              type="text"
              placeholder="Search tickets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-[#3c3836] border border-[#504945] rounded-lg text-[#ebdbb2] placeholder-[#928374] focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent"
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 bg-[#3c3836] border border-[#504945] rounded-lg text-[#ebdbb2] focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>

          {/* Priority Filter */}
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className="px-4 py-2 bg-[#3c3836] border border-[#504945] rounded-lg text-[#ebdbb2] focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent"
          >
            <option value="all">All Priority</option>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>

          {/* Refresh Button */}
          <button
            onClick={loadTickets}
            disabled={loading}
            className="px-4 py-2 bg-[#3c3836] text-[#ebdbb2] rounded-lg hover:bg-[#504945] transition-colors flex items-center justify-center space-x-2 border border-[#504945]"
          >
            {loading ? (
              <FaSpinner className="animate-spin" />
            ) : (
              <>
                <FaSearch size={14} />
                <span>Refresh</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Ticket List */}
      <div className="bg-[#282828] rounded-xl shadow-sm border border-[#3c3836] overflow-hidden">
        {filteredTickets.length === 0 ? (
          <div className="p-12 text-center text-[#928374]">
            <p className="text-lg">No tickets found</p>
            {tickets.length === 0 && (
              <button
                onClick={() => setShowNewTicketModal(true)}
                className="mt-4 px-4 py-2 bg-[#fabd2f] text-[#1d2021] rounded-lg hover:bg-[#fe8019] font-medium"
              >
                Create First Ticket
              </button>
            )}
          </div>
        ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#3c3836]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#bdae93] uppercase tracking-wider">
                  Ticket
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#bdae93] uppercase tracking-wider">
                  Customer
                </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#bdae93] uppercase tracking-wider">
                    Agent
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#bdae93] uppercase tracking-wider">
                  Priority
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#bdae93] uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#bdae93] uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#bdae93] uppercase tracking-wider">
                  Last Updated
                </th>
              </tr>
            </thead>
            <tbody className="bg-[#282828] divide-y divide-[#3c3836]">
              {filteredTickets.map((ticket) => (
                <tr key={ticket.id} className="hover:bg-[#3c3836]">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-gradient-to-r from-[#fabd2f] to-[#fe8019] rounded-full flex items-center justify-center text-[#1d2021] font-bold">
                        T
                      </div>
                      <div className="ml-4">
                          <button
                            onClick={() => viewTicket(ticket)}
                            className="text-sm font-medium text-[#fabd2f] hover:text-[#fe8019] hover:underline text-left"
                          >
                            {ticket.subject}
                          </button>
                          <div className="text-sm text-[#bdae93]">#{ticket.id}</div>
                          <div className="text-xs text-[#928374]">{ticket.responseCount} {ticket.responseCount === 1 ? 'response' : 'responses'}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-[#ebdbb2]">{ticket.customer}</div>
                    <div className="text-sm text-[#bdae93]">{ticket.customerEmail}</div>
                  </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        value={ticket.assignedTo || ''}
                        onChange={(e) => {
                          const newAssignee = e.target.value || null;
                          assignTicket(ticket.id, newAssignee);
                        }}
                        className="text-sm border border-[#504945] rounded px-2 py-1 focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent bg-[#3c3836] text-[#ebdbb2] min-w-[150px]"
                        disabled={saving}
                      >
                        <option value="">Unassigned</option>
                        {availableAgents.map((agent) => (
                          <option key={agent} value={agent}>
                            {agent}
                          </option>
                        ))}
                      </select>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
                      {getPriorityIcon(ticket.priority)}
                        <span className="ml-1 capitalize">{ticket.priority}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        value={ticket.status}
                        onChange={(e) => {
                          updateTicketStatus(ticket.id, e.target.value);
                        }}
                        className={`text-xs font-medium border border-[#504945] rounded px-2 py-1 focus:ring-2 focus:ring-[#fabd2f] bg-[#3c3836] text-[#ebdbb2] ${getStatusColor(ticket.status)}`}
                        disabled={saving}
                      >
                        <option value="open">Open</option>
                        <option value="in_progress">In Progress</option>
                        <option value="resolved">Resolved</option>
                        <option value="closed">Closed</option>
                      </select>
                  </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[#bdae93] capitalize">
                    {ticket.category}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[#928374]">
                      {formatDate(ticket.lastActivityAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>

      {/* Ticket Details Modal */}
      {showTicketModal && selectedTicket && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#282828] rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-[#3c3836]">
            <div className="p-6 border-b border-[#3c3836] sticky top-0 bg-[#282828] z-10">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-[#ebdbb2]">{selectedTicket.subject}</h2>
                  <p className="text-[#bdae93]">{selectedTicket.id}</p>
                </div>
                <button
                  onClick={() => {
                    if (hasUnsavedChanges) {
                      if (!confirm('You have unsaved changes. Are you sure you want to close?')) {
                        return;
                      }
                    }
                    setShowTicketModal(false);
                    setSelectedTicket(null);
                    setNewComment('');
                    setIsInternalComment(false);
                    setPendingStatusChange(null);
                    setPendingPriorityChange(null);
                    setStatusChangeComment('');
                    setHasUnsavedChanges(false);
                  }}
                  className="text-[#928374] hover:text-[#bdae93]"
                >
                  <FaTimes size={24} />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {loadingTicket ? (
                <div className="flex items-center justify-center h-64">
                  <FaSpinner className="animate-spin text-[#0A2463] text-2xl" />
                </div>
              ) : (
                <>
              {/* Ticket Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-[#ebdbb2] mb-4">Customer Information</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-[#928374]">Name</label>
                      <p className="text-[#ebdbb2]">{selectedTicket.customer}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#928374]">Email</label>
                          <p className="text-[#ebdbb2]">{selectedTicket.customerEmail || 'N/A'}</p>
                    </div>
                        {selectedTicket.accountNumber && (
                          <div>
                            <label className="text-sm font-medium text-[#928374]">Account Number</label>
                            <p className="text-[#ebdbb2]">{selectedTicket.accountNumber}</p>
                          </div>
                        )}
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-[#ebdbb2] mb-4">Ticket Details</h3>
                  <div className="space-y-3">
                        <div>
                          <label className="text-sm font-medium text-[#928374]">Priority</label>
                          <div className="mt-1">
                            <select
                              value={pendingPriorityChange !== null ? pendingPriorityChange : selectedTicket.priority}
                              onChange={(e) => {
                                const newPriority = e.target.value;
                                setPendingPriorityChange(newPriority);
                                setHasUnsavedChanges(true);
                              }}
                              className="text-sm font-medium border border-[#504945] rounded px-3 py-2 focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent bg-[#3c3836] text-[#ebdbb2] w-full"
                              disabled={saving}
                            >
                              <option value="low">Low</option>
                              <option value="medium">Medium</option>
                              <option value="high">High</option>
                              <option value="urgent">Urgent</option>
                            </select>
                          </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#928374]">Status</label>
                          <div className="mt-1">
                            <select
                              value={pendingStatusChange !== null ? pendingStatusChange : selectedTicket.status}
                              onChange={(e) => {
                                const newStatus = e.target.value;
                                setPendingStatusChange(newStatus);
                                setHasUnsavedChanges(true);
                                // Clear comment when status changes
                                if (newStatus !== selectedTicket.status) {
                                  setStatusChangeComment('');
                                }
                              }}
                              className="text-sm font-medium border border-[#504945] rounded px-3 py-2 focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent bg-[#3c3836] text-[#ebdbb2] w-full"
                              disabled={saving}
                            >
                              <option value="open">Open</option>
                              <option value="in_progress">In Progress</option>
                              <option value="resolved">Resolved</option>
                              <option value="closed">Closed</option>
                            </select>
                          </div>
                          {/* Status Change Comment - Required when status changes */}
                          {pendingStatusChange !== null && pendingStatusChange !== selectedTicket.status && (
                            <div className="mt-3">
                              <label className="block text-sm font-medium text-[#bdae93] mb-2">
                                Comment <span className="text-red-500">*</span> (Required for status change)
                              </label>
                              <textarea
                                value={statusChangeComment}
                                onChange={(e) => setStatusChangeComment(e.target.value)}
                                rows={3}
                                className="w-full px-3 py-2 bg-[#1d2021] border border-[#504945] rounded-lg text-[#ebdbb2] focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent"
                                placeholder="Please explain the reason for this status change..."
                                required
                              />
                            </div>
                          )}
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#928374]">Category</label>
                          <p className="text-[#ebdbb2] capitalize">{selectedTicket.category}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-[#928374]">Assigned To</label>
                          <p className="text-[#ebdbb2]">{selectedTicket.assignedTo || 'Unassigned'}</p>
                          <button
                            onClick={() => {
                              const newAssignee = prompt('Enter admin email (or leave empty to unassign):');
                              if (newAssignee !== null) {
                                assignTicket(selectedTicket.ticketId, newAssignee.trim() || null);
                              }
                            }}
                            className="mt-1 text-xs text-blue-600 hover:underline"
                          >
                            {selectedTicket.assignedTo ? 'Change Assignment' : 'Assign Ticket'}
                          </button>
                    </div>
                  </div>
                </div>
                
                {selectedTicket.metadata?.planChange && (
                  <div className="mb-6 border border-[#fabd2f]/40 bg-[#fabd2f]/5 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-[#fabd2f] mb-3">
                      Speed change request
                      <span className="ml-2 text-xs uppercase tracking-wide px-2 py-0.5 rounded bg-[#504945] text-[#ebdbb2]">
                        {selectedTicket.metadata.planChange.direction}
                      </span>
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                      {[
                        ['From', `${selectedTicket.metadata.planChange.currentPlan || '—'} · $${Number(selectedTicket.metadata.planChange.currentPrice ?? 0).toFixed(2)}/mo`],
                        ['To', `${selectedTicket.metadata.planChange.requestedPlan || '—'} · $${Number(selectedTicket.metadata.planChange.requestedPrice ?? 0).toFixed(2)}/mo`],
                        ['Vocus speed tier', selectedTicket.metadata.planChange.requestedSpeedTier || 'not set on the Plans row'],
                        ['Vocus ServiceID', selectedTicket.metadata.planChange.vocusServiceId || 'none on record'],
                        ['Current PlanID', selectedTicket.metadata.planChange.vocusPlanId || 'unknown'],
                        ['Preferred start', selectedTicket.metadata.planChange.preferredTiming === 'asap' ? 'As soon as possible' : 'From the next billing cycle'],
                        ['CIS acknowledged', `${selectedTicket.metadata.planChange.cisVersion || '—'} at ${selectedTicket.metadata.planChange.cisAcknowledgedAt || '—'}`],
                      ].map(([label, value]) => (
                        <div key={label}>
                          <label className="text-xs font-medium text-[#928374]">{label}</label>
                          <p className="text-[#ebdbb2] break-words">{value}</p>
                        </div>
                      ))}
                    </div>
                    <p className="mt-3 pt-3 border-t border-[#504945] text-xs text-[#928374] leading-relaxed">
                      Order the change in the Vocus portal (FIBRE::Set scope PLAN isn&apos;t wired up here yet).
                      Only once it completes, set the customer&apos;s plan <em>and</em> Base monthly price in
                      Customer Details &rarr; Pricing &mdash; changing one without the other leaves the invoice wrong.
                      Downgrades: schedule for month end, or credit the prepaid difference with a one-cycle
                      override. One change per Service per calendar month (Vocus NBN Connect cl.18.2/18.3).
                    </p>
                  </div>
                )}

                <div>
                  <h3 className="text-lg font-semibold text-[#ebdbb2] mb-4">Timeline</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-[#928374]">Created</label>
                          <p className="text-[#ebdbb2]">{formatDate(selectedTicket.createdAt)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#928374]">Last Updated</label>
                          <p className="text-[#ebdbb2]">{formatDate(selectedTicket.updatedAt)}</p>
                    </div>
                    <div>
                          <label className="text-sm font-medium text-[#928374]">Comments</label>
                      <p className="text-[#ebdbb2]">{selectedTicket.responseCount}</p>
                    </div>
                        {selectedTicket.resolvedAt && (
                          <div>
                            <label className="text-sm font-medium text-[#928374]">Resolved</label>
                            <p className="text-[#ebdbb2]">{formatDate(selectedTicket.resolvedAt)}</p>
                  </div>
                        )}
                        {selectedTicket.closedAt && (
                          <div>
                            <label className="text-sm font-medium text-[#928374]">Closed</label>
                            <p className="text-[#ebdbb2]">{formatDate(selectedTicket.closedAt)}</p>
                          </div>
                        )}
                      </div>
                </div>
              </div>

              {/* Description */}
              <div>
                <h3 className="text-lg font-semibold text-[#ebdbb2] mb-4">Description</h3>
                <div className="bg-[#3c3836] p-4 rounded-lg">
                      <p className="text-[#ebdbb2] whitespace-pre-wrap">{selectedTicket.description}</p>
                </div>
              </div>

              {/* Tags */}
              {selectedTicket.tags && selectedTicket.tags.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-[#ebdbb2] mb-4">Tags</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedTicket.tags.map((tag, index) => (
                      <span key={index} className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

                  {/* Comments Section */}
                  <div>
                    <h3 className="text-lg font-semibold text-[#ebdbb2] mb-4">
                      Comments ({selectedTicket.comments?.length || 0})
                    </h3>
                    
                    {/* Comments List */}
                    <div className="space-y-4 mb-6">
                      {selectedTicket.comments && selectedTicket.comments.length > 0 ? (
                        selectedTicket.comments.map((comment, index) => (
                          <div
                            key={comment.commentId || index}
                            className={`p-4 rounded-lg border ${
                              comment.isInternal
                                ? 'bg-yellow-50 border-yellow-200'
                                : 'bg-[#3c3836] border-[#3c3836]'
                            }`}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <span className="font-medium text-[#ebdbb2]">{comment.createdBy || 'System'}</span>
                                {comment.isInternal && (
                                  <span className="ml-2 text-xs bg-yellow-200 text-yellow-800 px-2 py-1 rounded">
                                    Internal
                                  </span>
                                )}
                              </div>
                              <span className="text-xs text-[#928374]">{formatDate(comment.createdAt)}</span>
                            </div>
                            <p className="text-[#bdae93] whitespace-pre-wrap">{comment.comment}</p>
                          </div>
                        ))
                      ) : (
                        <p className="text-[#928374] text-center py-4">No comments yet</p>
                      )}
                    </div>

                    {/* Add Comment Form */}
                    <div className="border-t border-[#3c3836] pt-4">
                      <h4 className="text-md font-semibold text-[#ebdbb2] mb-3">Add Comment</h4>
                      <textarea
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        rows={4}
                        className="w-full px-3 py-2 bg-[#1d2021] border border-[#504945] rounded-lg text-[#ebdbb2] focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent mb-3"
                        placeholder="Enter your comment..."
                      />
                      <div className="flex items-center justify-between">
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={isInternalComment}
                            onChange={(e) => setIsInternalComment(e.target.checked)}
                            className="rounded border-[#504945] text-[#0A2463] focus:ring-[#fabd2f]"
                          />
                          <span className="text-sm text-[#bdae93]">Internal comment (visible only to admins)</span>
                        </label>
                        <button
                          onClick={addComment}
                          disabled={!newComment.trim() || saving}
                          className="px-4 py-2 bg-[#fabd2f] text-[#1d2021] rounded-lg hover:bg-[#fe8019] disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                        >
                          {saving ? (
                            <FaSpinner className="animate-spin" />
                          ) : (
                            <>
                              <FaReply size={14} />
                              <span>Add Comment</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-6 border-t border-[#3c3836]">
                <div className="flex items-center space-x-4">
                  {hasUnsavedChanges && (
                    <span className="text-sm text-amber-600">You have unsaved changes</span>
                  )}
                </div>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={loadTickets}
                    className="px-4 py-2 border border-[#504945] text-[#bdae93] rounded-lg hover:bg-[#3c3836]"
                    disabled={saving}
                  >
                    Refresh
                  </button>
                  <button
                    onClick={saveTicketChanges}
                    disabled={saving || !hasUnsavedChanges || (pendingStatusChange !== null && pendingStatusChange !== selectedTicket.status && !statusChangeComment.trim())}
                    className="px-6 py-2 bg-[#fabd2f] text-[#1d2021] rounded-lg hover:bg-[#fe8019] disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {saving ? (
                      <>
                        <FaSpinner className="animate-spin" />
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <FaCheck size={16} />
                        <span>Save Changes</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* New Ticket Modal */}
      {showNewTicketModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#282828] rounded-xl max-w-2xl w-full border border-[#3c3836]">
            <div className="p-6 border-b border-[#3c3836]">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-[#ebdbb2]">Create New Ticket</h2>
                <button
                  onClick={() => setShowNewTicketModal(false)}
                  className="text-[#928374] hover:text-[#bdae93]"
                >
                  <FaTimes size={24} />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#bdae93] mb-2">Subject *</label>
                <input
                  type="text"
                  value={newTicket.subject}
                  onChange={(e) => setNewTicket({...newTicket, subject: e.target.value})}
                  className="w-full px-3 py-2 bg-[#1d2021] border border-[#504945] rounded-lg text-[#ebdbb2] focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent"
                  placeholder="Enter ticket subject"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#bdae93] mb-2">Description *</label>
                <textarea
                  value={newTicket.description}
                  onChange={(e) => setNewTicket({...newTicket, description: e.target.value})}
                  rows={4}
                  className="w-full px-3 py-2 bg-[#1d2021] border border-[#504945] rounded-lg text-[#ebdbb2] focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent"
                  placeholder="Enter ticket description"
                  required
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#bdae93] mb-2">Priority</label>
                  <select
                    value={newTicket.priority}
                    onChange={(e) => setNewTicket({...newTicket, priority: e.target.value})}
                    className="w-full px-3 py-2 border border-[#504945] rounded-lg focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#bdae93] mb-2">Category</label>
                  <select
                    value={newTicket.category}
                    onChange={(e) => setNewTicket({...newTicket, category: e.target.value})}
                    className="w-full px-3 py-2 border border-[#504945] rounded-lg focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent"
                  >
                    <option value="general">General</option>
                    <option value="technical">Technical</option>
                    <option value="billing">Billing</option>
                    <option value="signup">Signup</option>
                    <option value="plan-change">Plan / speed change</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#bdae93] mb-2">Customer Email (Optional)</label>
                  <input
                    type="email"
                    value={newTicket.customerEmail}
                    onChange={(e) => setNewTicket({...newTicket, customerEmail: e.target.value})}
                    className="w-full px-3 py-2 border border-[#504945] rounded-lg focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent"
                    placeholder="customer@email.com"
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-end space-x-4 pt-6">
                <button
                  onClick={() => setShowNewTicketModal(false)}
                  className="px-4 py-2 border border-[#504945] text-[#bdae93] rounded-lg hover:bg-[#3c3836]"
                >
                  Cancel
                </button>
                <button
                  onClick={createNewTicket}
                  disabled={saving || !newTicket.subject.trim() || !newTicket.description.trim()}
                  className="px-4 py-2 bg-[#fabd2f] text-[#1d2021] rounded-lg hover:bg-[#fe8019] disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {saving ? (
                    <FaSpinner className="animate-spin" />
                  ) : (
                    <>
                      <FaPlus size={14} />
                      <span>Create Ticket</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketSystem; 